import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, Check, X, MapPin, Mail, Phone, Globe, Users, Database, Download, ChevronDown, ChevronRight, Shield, Zap } from "lucide-react";
import { toast } from "sonner";

const SUB_TABS = ["sweep0", "outreach", "queue", "phase4", "enrichment", "google"];
const SUB_TAB_LABELS = { sweep0: "Sweep 0 Coverage", outreach: "Outreach Database", queue: "Queue Status", phase4: "Phase 4 Slots", enrichment: "Enrichment Queue", google: "Google Validation" };

export default function OutreachInfrastructurePanel() {
  const [subTab, setSubTab] = useState("sweep0");
  const [loading, setLoading] = useState(true);
  const [sosData, setSosData] = useState(null);
  const [sosCandidates, setSosCandidates] = useState(null);
  const [socrataData, setSocrataData] = useState(null);
  const [odsData, setOdsData] = useState(null);
  const [datagovData, setDatagovData] = useState(null);
  const [outreachStats, setOutreachStats] = useState(null);
  const [queueSummary, setQueueSummary] = useState(null);
  const [phase4Data, setPhase4Data] = useState(null);
  const [phase5Data, setPhase5Data] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [googleData, setGoogleData] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedState, setExpandedState] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sos, candidates, socrata, ods, datagov] = await Promise.all([
        axios.get(`${API}/admin/sos-adapters`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/sos-adapter-candidates`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/socrata-cities`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/opendatasoft-cities`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/datagov-status`).catch(() => ({ data: null })),
      ]);
      setSosData(sos.data);
      setSosCandidates(candidates.data);
      setSocrataData(socrata.data);
      setOdsData(ods.data);
      setDatagovData(datagov.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchOutreach = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/outreach-stats`);
      setOutreachStats(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/enrichment-queue-summary`);
      setQueueSummary(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchPhase4 = async () => {
    setLoading(true);
    try {
      const [p4, credits] = await Promise.all([
        axios.get(`${API}/admin/pipeline/phase4-status`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/pipeline/credit-tracking`).catch(() => ({ data: null })),
      ]);
      setPhase4Data(p4.data);
      setCreditData(credits.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchPhase5 = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pipeline/phase5-priority-queue`);
      setPhase5Data(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchGoogle = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pipeline/google-validation`);
      setGoogleData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleGoogleApprove = async (entityId) => {
    setActionLoading(entityId);
    try {
      await axios.post(`${API}/admin/pipeline/google-validation/approve`, { entity_id: entityId });
      toast.success("Approved");
      fetchGoogle();
    } catch { toast.error("Failed"); }
    setActionLoading(null);
  };

  const handleGoogleReject = async (entityId) => {
    setActionLoading(entityId);
    try {
      await axios.post(`${API}/admin/pipeline/google-validation/reject`, { entity_id: entityId });
      toast.success("Rejected");
      fetchGoogle();
    } catch { toast.error("Failed"); }
    setActionLoading(null);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (subTab === "outreach" && !outreachStats) fetchOutreach();
    if (subTab === "queue" && !queueSummary) fetchQueue();
    if (subTab === "phase4" && !phase4Data) fetchPhase4();
    if (subTab === "enrichment" && !phase5Data) fetchPhase5();
    if (subTab === "google" && !googleData) fetchGoogle();
  }, [subTab]);

  const handleApprove = async (stateCode) => {
    setActionLoading(stateCode);
    try {
      await axios.post(`${API}/admin/sos-adapter-candidates/${stateCode}/approve`);
      toast.success(`Approved ${stateCode} adapter`);
      fetchAll();
    } catch { toast.error("Approval failed"); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (stateCode) => {
    setActionLoading(stateCode);
    try {
      await axios.post(`${API}/admin/sos-adapter-candidates/${stateCode}/reject`);
      toast.success(`Rejected ${stateCode}`);
      fetchAll();
    } catch { toast.error("Rejection failed"); }
    finally { setActionLoading(null); }
  };

  const statusColor = (s) => s === "active" ? "bg-emerald-100 text-emerald-700" : s === "placeholder" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

  if (loading && !sosData) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  return (
    <div className="space-y-4" data-testid="outreach-infrastructure-panel">
      <div className="flex gap-1.5" data-testid="outreach-subtabs">
        {SUB_TABS.map(k => (
          <button key={k} onClick={() => setSubTab(k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${subTab === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`outreach-tab-${k}`}>
            {SUB_TAB_LABELS[k]}
          </button>
        ))}
      </div>

      {/* ═══ SWEEP 0 COVERAGE TAB ═══ */}
      {subTab === "sweep0" && (
        <div className="space-y-6" data-testid="sweep0-coverage">
          {/* SOS State Coverage */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Shield className="w-4 h-4 text-indigo-500" /> SOS State Coverage ({sosData?.adapter_count || 0} jurisdictions)</h3>
            <div className="flex gap-3 mb-3 text-[10px] font-bold">
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">{sosData?.active_count || 0} Active</span>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{sosData?.placeholder_count || 0} Placeholder</span>
            </div>
            <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-400 font-semibold">State</th>
                    <th className="px-3 py-2 text-left text-slate-400 font-semibold">Code</th>
                    <th className="px-3 py-2 text-center text-slate-400 font-semibold">Status</th>
                    <th className="px-3 py-2 text-center text-slate-400 font-semibold">Strategy</th>
                    <th className="px-3 py-2 text-center text-slate-400 font-semibold">Demand</th>
                  </tr>
                </thead>
                <tbody>
                  {(sosData?.adapters || []).map(a => (
                    <tr key={a.state_code} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setExpandedState(expandedState === a.state_code ? null : a.state_code)} data-testid={`sos-row-${a.state_code}`}>
                      <td className="px-3 py-2 text-slate-700 font-medium">{a.state_name}</td>
                      <td className="px-3 py-2 text-slate-500">{a.state_code}</td>
                      <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor(a.adapter_status)}`}>{a.adapter_status}</span></td>
                      <td className="px-3 py-2 text-center text-slate-500">{a.search_strategy}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{a.demand_signal_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* SOS Adapter Candidates */}
          {sosCandidates && sosCandidates.count > 0 && (
            <div data-testid="sos-candidates-section">
              <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> SOS Adapter Candidates ({sosCandidates.count})</h3>
              <div className="space-y-2">
                {sosCandidates.candidates.map(c => (
                  <div key={c.state_code} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between" data-testid={`sos-candidate-${c.state_code}`}>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{c.state_name} ({c.state_code})</p>
                      <p className="text-[10px] text-slate-400">{c.status} | Confidence: {((c.turbo_confidence || 0) * 100).toFixed(0)}%</p>
                    </div>
                    {c.status === "pending_review" && (
                      <div className="flex gap-1.5">
                        <button onClick={() => handleApprove(c.state_code)} disabled={actionLoading === c.state_code} className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[10px] font-bold hover:bg-emerald-600 transition flex items-center gap-1" data-testid={`sos-approve-${c.state_code}`}>
                          {actionLoading === c.state_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                        </button>
                        <button onClick={() => handleReject(c.state_code)} disabled={actionLoading === c.state_code} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold hover:bg-slate-200 transition flex items-center gap-1" data-testid={`sos-reject-${c.state_code}`}>
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Socrata City Coverage */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Database className="w-4 h-4 text-sky-500" /> Socrata Cities ({socrataData?.city_count || 0})</h3>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl">
              <div className="grid grid-cols-3 gap-0.5 p-2 text-[10px]">
                {(socrataData?.cities || []).map(c => (
                  <div key={c.city} className="px-2 py-1 bg-slate-50 rounded text-slate-600">{c.city}, {c.state}</div>
                ))}
              </div>
            </div>
          </div>

          {/* OpenDataSoft Coverage */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Database className="w-4 h-4 text-violet-500" /> OpenDataSoft Cities ({odsData?.city_count || 0}) <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${odsData?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{odsData?.enabled ? "ON" : "OFF"}</span></h3>
            <div className="max-h-28 overflow-y-auto border border-slate-200 rounded-xl">
              <div className="grid grid-cols-3 gap-0.5 p-2 text-[10px]">
                {(odsData?.cities || []).map(c => (
                  <div key={c.city} className="px-2 py-1 bg-slate-50 rounded text-slate-600">{c.city}, {c.state}</div>
                ))}
              </div>
            </div>
          </div>

          {/* data.gov */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Globe className="w-4 h-4 text-teal-500" /> data.gov Federal Portal <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${datagovData?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{datagovData?.enabled ? "ON" : "OFF"}</span></h3>
            <p className="text-[10px] text-slate-400">{datagovData?.notes || "Federal CKAN API for business license datasets"}</p>
          </div>
        </div>
      )}

      {/* ═══ OUTREACH DATABASE TAB ═══ */}
      {subTab === "outreach" && (
        <div className="space-y-4" data-testid="outreach-database">
          {!outreachStats ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-xl p-3 bg-white border border-slate-200 text-center">
                  <p className="text-xl font-black text-slate-800">{outreachStats.total_businesses || 0}</p>
                  <p className="text-[10px] text-slate-500 font-semibold">Total Businesses</p>
                </div>
                <div className="rounded-xl p-3 bg-white border border-slate-200 text-center">
                  <p className="text-xl font-black text-emerald-600">{outreachStats.email_coverage_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 font-semibold flex items-center justify-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                </div>
                <div className="rounded-xl p-3 bg-white border border-slate-200 text-center">
                  <p className="text-xl font-black text-sky-600">{outreachStats.phone_coverage_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 font-semibold flex items-center justify-center gap-1"><Phone className="w-3 h-3" /> Phone</p>
                </div>
                <div className="rounded-xl p-3 bg-white border border-slate-200 text-center">
                  <p className="text-xl font-black text-violet-600">{outreachStats.website_coverage_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 font-semibold flex items-center justify-center gap-1"><Globe className="w-3 h-3" /> Website</p>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-600 mb-2">By Outreach Status</h4>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(outreachStats.by_status || {}).map(([k, v]) => (
                    <span key={k} className="px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-[10px] font-bold text-slate-600">{k}: <span className="text-slate-800">{v}</span></span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-600 mb-2">Owner Name Coverage</h4>
                <p className="text-sm font-bold text-slate-800">{outreachStats.owner_name_coverage_pct || 0}% <span className="text-[10px] text-slate-400 font-normal">({outreachStats.with_owner_name || 0} / {outreachStats.total_businesses || 0})</span></p>
              </div>

              <button onClick={() => toast.info("Export not available in preview")} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl hover:bg-slate-200 transition text-xs font-bold text-slate-700" data-testid="outreach-export-btn">
                <Download className="w-4 h-4" /> Export Filtered Results (CSV)
              </button>
            </>
          )}
        </div>
      )}

      {/* ═══ QUEUE STATUS TAB ═══ */}
      {subTab === "queue" && (
        <div className="space-y-3" data-testid="queue-status">
          {!queueSummary ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : Object.entries(queueSummary).map(([source, stats]) => (
            <div key={source} className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={`queue-source-${source}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-800 capitalize">{source.replace("_", " ")}</h4>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${stats.credits_limit > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{stats.credits_limit > 0 ? "Active" : "Disabled"}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-lg font-black text-slate-800">{stats.credits_used || 0}</p>
                  <p className="text-[10px] text-slate-400">Credits Used</p>
                </div>
                <div>
                  <p className="text-lg font-black text-slate-800">{stats.credits_remaining || 0}</p>
                  <p className="text-[10px] text-slate-400">Remaining</p>
                </div>
                <div>
                  <p className="text-lg font-black text-amber-600">{stats.pending || 0}</p>
                  <p className="text-[10px] text-slate-400">Pending</p>
                </div>
                <div>
                  <p className="text-lg font-black text-emerald-600">{stats.completed_this_month || 0}</p>
                  <p className="text-[10px] text-slate-400">Completed</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Est. backlog: {stats.backlog_estimate_months || 0} months at current rate</p>
            </div>
          ))}
        </div>
      )}

      {/* ═══ PHASE 4 SLOTS TAB ═══ */}
      {subTab === "phase4" && (
        <div className="space-y-3" data-testid="phase4-status">
          {!phase4Data ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : (
            <>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Phase 4 Active Slots (max 3)</h4>
                {phase4Data.active_slots?.length > 0 ? (
                  <div className="space-y-2">
                    {phase4Data.active_slots.map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 bg-sky-50 rounded-lg">
                        <span className="text-xs font-bold text-sky-800">Slot {s.rolling_pipeline_slot}: {s.city_name || s.city_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.phase_status?.phase_4?.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          P4: {s.phase_status?.phase_4?.status || "—"} / P5: {s.phase_status?.phase_5?.status || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">No cities in Phase 4</p>}
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Precision Validation Queue</h4>
                {Object.keys(phase4Data.precision_validation_by_city || {}).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(phase4Data.precision_validation_by_city).map(([city, count]) => (
                      <div key={city} className="flex justify-between text-xs px-2 py-1 bg-slate-50 rounded">
                        <span className="text-slate-600">{city}</span>
                        <span className="font-bold text-slate-800">{count} pending</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">No pending validations</p>}
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Daily Budget & Circuit Breakers</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(phase4Data.daily_budgets || {}).map(([source, info]) => (
                    <div key={source} className="px-3 py-2 bg-slate-50 rounded-lg">
                      <p className="text-xs font-bold text-slate-700 capitalize">{source.replace(/_/g, " ")}</p>
                      <p className="text-sm font-black text-slate-800">{info.used_today} used</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${info.circuit_breaker ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {info.circuit_breaker ? "TRIPPED" : "Active"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ ENRICHMENT QUEUE TAB ═══ */}
      {subTab === "enrichment" && (
        <div className="space-y-3" data-testid="enrichment-queue">
          {!phase5Data ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : (
            <>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Top Priority Records</h4>
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {(phase5Data.top_priority_records || []).map((r, i) => (
                    <div key={r.entity_id || i} className="flex items-center justify-between px-2 py-1.5 bg-slate-50 rounded text-xs">
                      <div className="truncate flex-1 mr-2">
                        <span className="font-bold text-slate-700">{r.raw_name}</span>
                        <span className="text-slate-400 ml-1">({r.city_id})</span>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <span className="font-mono text-amber-600">C:{(r.confidence_score || 0).toFixed(2)}</span>
                        <span className="font-mono text-pink-600">B:{(r.birthday_likelihood_score || 0).toFixed(2)}</span>
                        <span className="font-mono font-bold text-sky-700">P:{(r.priority_score || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  {(!phase5Data.top_priority_records || phase5Data.top_priority_records.length === 0) && (
                    <p className="text-xs text-slate-400">No records in enrichment queue</p>
                  )}
                </div>
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Nightly Caps & Circuit Breakers</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(phase5Data.nightly_caps || {}).map(([source, info]) => (
                    <div key={source} className="px-3 py-2 bg-slate-50 rounded-lg">
                      <p className="text-xs font-bold text-slate-700 capitalize">{source.replace(/_/g, " ")}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm font-black text-slate-800">{info.used_tonight}</span>
                        <span className="text-[10px] text-slate-400">/ {info.remaining === "unlimited" ? "unlimited" : (info.nightly_cap || "—")}</span>
                      </div>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${info.circuit_breaker ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {info.circuit_breaker ? "TRIPPED" : "Active"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ GOOGLE VALIDATION TAB ═══ */}
      {subTab === "google" && (
        <div className="space-y-3" data-testid="google-validation">
          {!googleData ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Nightly Limit</p>
                  <p className="text-xl font-black text-slate-800">{googleData.current_nightly_limit}</p>
                </div>
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Spend Today</p>
                  <p className="text-xl font-black text-emerald-700">${googleData.spend_today_usd?.toFixed(2)}</p>
                </div>
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Month Spend</p>
                  <p className="text-xl font-black text-amber-700">${googleData.spend_month_usd?.toFixed(2)}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Credit Left</p>
                  <p className="text-lg font-black text-sky-700">${googleData.remaining_credit_usd?.toFixed(2)}</p>
                </div>
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">Days Until Exhausted</p>
                  <p className={`text-lg font-black ${googleData.estimated_days_until_exhausted < 10 ? "text-red-600" : "text-slate-800"}`}>
                    {googleData.estimated_days_until_exhausted === 999 ? "N/A" : googleData.estimated_days_until_exhausted}
                  </p>
                </div>
                <div className="border border-slate-200 rounded-xl p-3 bg-white text-center">
                  <p className="text-[10px] text-slate-400 uppercase">7-Day Avg ROI</p>
                  <p className="text-lg font-black text-violet-700">{googleData.avg_roi_7day?.toFixed(3)}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="px-3 py-2 bg-amber-50 rounded-lg text-center">
                  <p className="text-[10px] text-amber-600">Pending</p>
                  <p className="text-lg font-black text-amber-800">{googleData.pending_approval}</p>
                </div>
                <div className="px-3 py-2 bg-sky-50 rounded-lg text-center">
                  <p className="text-[10px] text-sky-600">Approved</p>
                  <p className="text-lg font-black text-sky-800">{googleData.backlog_approved}</p>
                </div>
                <div className="px-3 py-2 bg-emerald-50 rounded-lg text-center">
                  <p className="text-[10px] text-emerald-600">Completed</p>
                  <p className="text-lg font-black text-emerald-800">{googleData.completed}</p>
                </div>
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <h4 className="text-sm font-bold text-slate-800 mb-3">Pending Approval (Top 10)</h4>
                {(googleData.top_pending || []).length > 0 ? (
                  <div className="space-y-2">
                    {googleData.top_pending.map((r, i) => (
                      <div key={r.entity_id || i} className="flex items-center justify-between px-2 py-1.5 bg-slate-50 rounded">
                        <div className="flex-1 mr-2">
                          <span className="text-xs font-bold text-slate-700">{r.city_id}</span>
                          <span className="text-[10px] text-slate-400 ml-2">{r.reason}</span>
                          <span className="text-[10px] font-mono text-sky-600 ml-2">P:{(r.priority_score || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            data-testid={`google-approve-${r.entity_id}`}
                            onClick={() => handleGoogleApprove(r.entity_id)}
                            disabled={actionLoading === r.entity_id}
                            className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] rounded font-bold hover:bg-emerald-600"
                          >
                            {actionLoading === r.entity_id ? "..." : "Approve"}
                          </button>
                          <button
                            data-testid={`google-reject-${r.entity_id}`}
                            onClick={() => handleGoogleReject(r.entity_id)}
                            disabled={actionLoading === r.entity_id}
                            className="px-2 py-0.5 bg-red-500 text-white text-[10px] rounded font-bold hover:bg-red-600"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">No pending Google validation requests</p>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
